import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type NavigationGuardWithThis,
  type RouteLocationNormalizedGeneric
} from 'vue-router'
import { auth } from '@/stores/auth'
import NProgress from 'nprogress'
import colors from 'tailwindcss/colors'
import { sendPageVisit } from '@/services/tracking'
import { context } from '@/services'
import { getOrCreateSession } from '@/services/tracking-sessions'
import { useGlobalStore } from '@/stores/global'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // * Public Routes
    {
      path: '/health',
      name: 'health',
      meta: {
        public: true
      },
      component: {
        render: () => 'up'
      }
    },
    {
      path: '/books',
      name: 'library',
      meta: {
        public: true
      },
      component: () => import('../views/Library.vue')
    },
    {
      path: '/privacy',
      name: 'privacy',
      meta: {
        public: true
      },
      component: () => import('../views/Privacy.vue')
    }
  ]
})

const adminAuth = ({
  to,
  from,
  next
}: {
  to: RouteLocationNormalizedGeneric
  from: RouteLocationNormalizedGeneric
  next: NavigationGuardNext
}) => {
  const admin = localStorage.getItem('admin')
    ? JSON.parse(localStorage.getItem('admin') as string)
    : null

  if (to.meta.requiresAuth && !admin) {
    return next({ name: 'admin-login' })
  }
  if (to.meta.guest && admin) {
    return next({ name: 'admin-visits' })
  }

  return next()
}

router.beforeEach(async (to, from, next) => {
  NProgress.start()

  const loader = document.getElementById('initial-loader')

  // Handle public routes - no auth required
  if (to.meta.public) {
    if (loader) {
      document.body.removeChild(loader as ChildNode)
    }
    return next()
  }

  if (to.name?.toString().startsWith('admin')) {
    if (loader) {
      document.body.removeChild(loader as ChildNode)
    }
    return adminAuth({ to, from, next })
  }

  const authStore = auth()
  await authStore.init()

  if (loader) {
    document.body.removeChild(loader as ChildNode)
  }
  if (to.meta.requiresAuth && !authStore.user) {
    console.log('not auth')

    return next({ name: 'login' })
  }

  if (to.meta.guest && authStore.user) {
    return next({ name: 'home' })
  }

  if (context == 'bayen_school') {
    document.documentElement.style.setProperty('--color-primary', '#d6336c')
    document.documentElement.style.setProperty('--color-background', '#faf1f4')
  }

  return next()
})

router.afterEach(async (to) => {
  NProgress.done()

  const authStore = auth()

  if (!authStore.user) return

  const trackingSessionId = await getOrCreateSession()

  const { visit } = await sendPageVisit(
    authStore.user.name,
    to.name as string,
    authStore.user.user_branches.current_branch_id,
    authStore.user.user_branches.current_branch_name,
    to.path,
    trackingSessionId
  )

  const globalStore = useGlobalStore()

  globalStore.currentVisitId = visit.id
})

export default router
